import { getAllLineItemServiceTypes } from '../constants/lineItemServiceTypes';

const LINE_ITEM_SEARCH_FIELDS = [
  ['title', 'TITLE'],
  ['description', 'DESCRIPTION'],
  ['startlocation', 'STARTLOCATION'],
  ['endlocation', 'ENDLOCATION'],
  ['vendorname', 'VENDORNAME'],
  ['contactname', 'CONTACTNAME'],
  ['vendorphone', 'VENDORPHONE'],
  ['vendorlink', 'VENDORLINK'],
  ['confirmationnumber', 'CONFIRMATIONNUMBER'],
  ['serviceclass', 'SERVICECLASS'],
  ['servicenumber', 'SERVICENUMBER']
];

const NAME_SEARCH_FIELD_KEYS = new Set([
  'HOTELNAME',
  'RESTAURANTNAME',
  'PROVIDERNAME',
  'COMPANYNAME',
  'RENTALCOMPANY',
  'TRAINCOMPANY',
  'AIRLINE',
  'VENDORNAME'
]);

export const LIBRARY_MATCH_TIERS = {
  EXACT_TITLE: 1,
  WHOLE_TITLE: 2,
  PARTIAL_TITLE: 3,
  NAME_FIELD: 4,
  OTHER_FIELD: 5
};

const NO_MATCH = -1;

const LIBRARY_GROUP_ALIASES = {
  day: ['day', 'days'],
  additional: ['content', 'additional content'],
  HTL: [
    'hotel',
    'hotels',
    'lodging',
    'accommodation',
    'accommodations',
    'inn',
    'resort',
    'hostel',
    'guesthouse',
    'bnb',
    'villa',
    'apartment',
    'chalet',
    'bungalow',
    'camp'
  ],
  TRF: [
    'transfer',
    'transfers',
    'pickup',
    'pick up',
    'dropoff',
    'drop off',
    'shuttle',
    'chauffeur'
  ],
  FLT: ['flight', 'flights', 'airline', 'airport'],
  TRN: ['train', 'trains', 'rail', 'station'],
  RST: [
    'restaurant',
    'restaurants',
    'dining',
    'dinner',
    'lunch',
    'breakfast',
    'cafe',
    'bistro',
    'tavern'
  ],
  ACT: [
    'activity',
    'activities',
    'tour',
    'excursion',
    'experience',
    'sightseeing',
    'museum',
    'gallery',
    'monument',
    'adventure'
  ],
  CAR: [
    'car',
    'cars',
    'car rental',
    'rental car',
    'car hire',
    'vehicle rental'
  ],
  INF: ['info', 'information']
};

export const MIN_LIBRARY_SEARCH_LENGTH = 3;

export const LIBRARY_GROUP_ORDER = [
  'day',
  'additional',
  'HTL',
  'TRF',
  'ACT',
  'FLT',
  'RST',
  'CAR',
  'TRN',
  'INF'
];

export const normalizeLibrarySearchText = (value = '') =>
  String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/ø/gi, (character) => (character === 'Ø' ? 'O' : 'o'))
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim();

const getWords = (value) =>
  normalizeLibrarySearchText(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const isTitleField = ({ key }) => key === 'TITLE';

const isNameField = ({ key }) => NAME_SEARCH_FIELD_KEYS.has(key);

// Search fields are temporary objects, so each field can tokenize lazily once
// without caching stale text on library items or rereading long descriptions.
const getFieldWords = (field) => (field.words ??= getWords(field.value));

const fieldHasWholeTerm = (field, term) => getFieldWords(field).includes(term);

const fieldHasPartialTerm = (field, term) =>
  getFieldWords(field).some((word) => word.startsWith(term));

const hasExactPhraseInTitle = (fields, terms) => {
  if (terms.length < 2) return false;

  return fields.filter(isTitleField).some((field) => {
    const words = getFieldWords(field);
    return words.some((_, index) =>
      terms.every((term, termIndex) => words[index + termIndex] === term)
    );
  });
};

const getTypeAliasMatches = (termEntries) => {
  const aliasEntries = Object.entries(LIBRARY_GROUP_ALIASES)
    .flatMap(([code, aliases]) =>
      aliases.map((alias) => ({
        code,
        words: getWords(alias)
      }))
    )
    .filter(({ words }) => words.length > 0)
    .sort(
      (a, b) =>
        b.words.length - a.words.length ||
        LIBRARY_GROUP_ORDER.indexOf(a.code) -
          LIBRARY_GROUP_ORDER.indexOf(b.code)
    );

  const consumedIndexes = new Set();
  const matches = [];
  const terms = termEntries.map(({ term }) => term);

  aliasEntries.forEach(({ code, words }) => {
    for (let start = 0; start <= terms.length - words.length; start += 1) {
      const indexes = words.map((_, index) => start + index);
      const overlapsConsumedAlias = indexes.some((index) =>
        consumedIndexes.has(index)
      );
      const isAliasMatch = words.every(
        (word, index) => terms[start + index] === word
      );

      if (!overlapsConsumedAlias && isAliasMatch) {
        indexes.forEach((index) => consumedIndexes.add(index));
        matches.push({ code, start });
      }
    }
  });

  return { consumedIndexes, matches };
};

const getDefaultGroupOrder = (code) => {
  const order = LIBRARY_GROUP_ORDER.indexOf(code);
  return order === -1 ? LIBRARY_GROUP_ORDER.length : order;
};

const uniqueByDefaultGroupOrder = (codes) =>
  [...new Set(codes)].sort(
    (a, b) => getDefaultGroupOrder(a) - getDefaultGroupOrder(b)
  );

const getLibrarySearchPlan = (query = '') => {
  const termEntries = getWords(query).map((term, index) => ({ term, index }));
  const { consumedIndexes, matches } = getTypeAliasMatches(termEntries);
  const nonTypeTerms = termEntries.filter(
    ({ index }) => !consumedIndexes.has(index)
  );
  const isTypeOnlySearch =
    termEntries.length > 0 &&
    consumedIndexes.size > 0 &&
    nonTypeTerms.length === 0;

  return {
    terms: termEntries,
    optionalTypeIndexes: isTypeOnlySearch ? new Set() : consumedIndexes,
    prioritizedGroupCodes: uniqueByDefaultGroupOrder(
      matches.map(({ code }) => code)
    )
  };
};

const getSearchFields = (item) => {
  if (Array.isArray(item?.searchfields)) {
    const searchFields = item.searchfields
      .map((field) => ({
        key: String(field?.key || '').toUpperCase(),
        value: field?.value
      }))
      .filter(({ value }) => Boolean(value));

    if (
      item?.title &&
      !searchFields.some(({ key, value }) => key === 'TITLE' && value)
    ) {
      searchFields.unshift({ key: 'TITLE', value: item.title });
    }

    return searchFields;
  }

  if (item?.kind === 'lineitem') {
    return LINE_ITEM_SEARCH_FIELDS.map(([property, key]) => ({
      key,
      value: item?.[property]
    })).filter(({ value }) => Boolean(value));
  }

  return [
    { key: 'TITLE', value: item?.title },
    { key: 'DESCRIPTION', value: item?.description }
  ].filter(({ value }) => Boolean(value));
};

const getTermTier = (fields, term) => {
  const titleFields = fields.filter(isTitleField);

  if (titleFields.some((field) => fieldHasWholeTerm(field, term))) {
    return LIBRARY_MATCH_TIERS.WHOLE_TITLE;
  }

  if (titleFields.some((field) => fieldHasPartialTerm(field, term))) {
    return LIBRARY_MATCH_TIERS.PARTIAL_TITLE;
  }

  if (
    fields.filter(isNameField).some((field) => fieldHasPartialTerm(field, term))
  ) {
    return LIBRARY_MATCH_TIERS.NAME_FIELD;
  }

  if (fields.some((field) => fieldHasPartialTerm(field, term))) {
    return LIBRARY_MATCH_TIERS.OTHER_FIELD;
  }

  return NO_MATCH;
};

const sortTermsBySearchOrder = (terms) =>
  [...terms].sort((a, b) => a.index - b.index);

const getTermValues = (termEntries) =>
  sortTermsBySearchOrder(termEntries).map(({ term }) => term);

const getTierForTerms = (fields, termEntries) => {
  if (termEntries.length === 0) return NO_MATCH;

  const terms = getTermValues(termEntries);
  if (hasExactPhraseInTitle(fields, terms)) {
    return LIBRARY_MATCH_TIERS.EXACT_TITLE;
  }

  const termTiers = terms.map((term) => getTermTier(fields, term));
  if (termTiers.includes(NO_MATCH)) return NO_MATCH;
  return Math.max(...termTiers);
};

const getBestTierForPlan = (fields, plan) => {
  // Type words may be absent, but any that match still participate in the
  // item's tier. Retain their query order for exact-phrase matching.
  const matchingTerms = plan.terms.filter(
    ({ index, term }) =>
      !plan.optionalTypeIndexes.has(index) ||
      getTermTier(fields, term) !== NO_MATCH
  );
  return getTierForTerms(fields, matchingTerms);
};

const getLibraryMatchTierForPlan = (item, plan) => {
  if (plan.terms.length === 0) return 0;
  const fields = getSearchFields(item);
  return getBestTierForPlan(fields, plan);
};

const getLibrarySearchEntries = (items, plan) =>
  items.map((item, index) => ({
    item,
    index,
    title: normalizeLibrarySearchText(item?.title),
    tier: getLibraryMatchTierForPlan(item, plan),
    groupCode: getLibraryItemGroupCode(item)
  }));

const getGroupStats = (entries) =>
  entries.reduce((stats, entry) => {
    if (!entry.groupCode || entry.tier < LIBRARY_MATCH_TIERS.EXACT_TITLE) {
      return stats;
    }

    const current = stats.get(entry.groupCode) || {
      bestTier: Number.POSITIVE_INFINITY,
      bestTierCount: 0
    };

    if (entry.tier < current.bestTier) {
      stats.set(entry.groupCode, {
        bestTier: entry.tier,
        bestTierCount: 1
      });
    } else if (entry.tier === current.bestTier) {
      stats.set(entry.groupCode, {
        ...current,
        bestTierCount: current.bestTierCount + 1
      });
    }

    return stats;
  }, new Map());

const compareGroupCodes = (stats) => (a, b) => {
  const aStats = stats.get(a) || {
    bestTier: Number.POSITIVE_INFINITY,
    bestTierCount: 0
  };
  const bStats = stats.get(b) || {
    bestTier: Number.POSITIVE_INFINITY,
    bestTierCount: 0
  };

  return (
    aStats.bestTier - bStats.bestTier ||
    bStats.bestTierCount - aStats.bestTierCount ||
    getDefaultGroupOrder(a) - getDefaultGroupOrder(b)
  );
};

const getOrderedGroupCodesFromEntries = (entries, plan) => {
  const presentCodes = new Set(entries.map(({ groupCode }) => groupCode));
  const orderedPresentCodes = LIBRARY_GROUP_ORDER.filter((code) =>
    presentCodes.has(code)
  );
  const stats = getGroupStats(entries);
  const compareWithStats = compareGroupCodes(stats);
  const prioritizedCodes = plan.prioritizedGroupCodes
    .filter((code) => presentCodes.has(code))
    .sort(compareWithStats);
  const prioritizedCodeSet = new Set(prioritizedCodes);
  const remainingCodes = orderedPresentCodes
    .filter((code) => !prioritizedCodeSet.has(code))
    .sort(compareWithStats);

  return [...prioritizedCodes, ...remainingCodes];
};

const getDefaultOrderedGroupCodes = (items) => {
  const presentCodes = new Set(items.map(getLibraryItemGroupCode));
  return LIBRARY_GROUP_ORDER.filter((code) => presentCodes.has(code));
};

const getGroupOrderMap = (groupCodes) =>
  new Map(groupCodes.map((code, index) => [code, index]));

const compareLibraryEntries = (groupOrder) => (a, b) => {
  const aGroupOrder = groupOrder.get(a.groupCode) ?? LIBRARY_GROUP_ORDER.length;
  const bGroupOrder = groupOrder.get(b.groupCode) ?? LIBRARY_GROUP_ORDER.length;

  return (
    aGroupOrder - bGroupOrder ||
    a.tier - b.tier ||
    a.title.localeCompare(b.title) ||
    a.index - b.index
  );
};

const compareLibraryEntriesByGroupOnly = (groupOrder) => (a, b) => {
  const aGroupOrder = groupOrder.get(a.groupCode) ?? LIBRARY_GROUP_ORDER.length;
  const bGroupOrder = groupOrder.get(b.groupCode) ?? LIBRARY_GROUP_ORDER.length;

  return aGroupOrder - bGroupOrder || a.index - b.index;
};

export const getLibraryMatchTier = (item, query) =>
  getLibraryMatchTierForPlan(item, getLibrarySearchPlan(query));

export const getLibraryMatchScore = (item, query) => {
  const tier = getLibraryMatchTier(item, query);
  if (tier <= 0) return tier;
  return LIBRARY_MATCH_TIERS.OTHER_FIELD + 1 - tier;
};

export const getOrderedLibraryGroupCodes = (items = [], query = '') => {
  if (normalizeLibrarySearchText(query).length < MIN_LIBRARY_SEARCH_LENGTH) {
    return getDefaultOrderedGroupCodes(items);
  }

  const plan = getLibrarySearchPlan(query);
  const entries = getLibrarySearchEntries(items, plan).filter(
    ({ tier }) => tier >= LIBRARY_MATCH_TIERS.EXACT_TITLE
  );

  return getOrderedGroupCodesFromEntries(entries, plan);
};

export const filterLibraryItems = (items = [], query = '') => {
  if (normalizeLibrarySearchText(query).length < MIN_LIBRARY_SEARCH_LENGTH) {
    return items;
  }

  const plan = getLibrarySearchPlan(query);
  const entries = getLibrarySearchEntries(items, plan).filter(
    ({ tier }) => tier >= LIBRARY_MATCH_TIERS.EXACT_TITLE
  );
  const groupOrder = getGroupOrderMap(
    getOrderedGroupCodesFromEntries(entries, plan)
  );

  return entries
    .sort(compareLibraryEntries(groupOrder))
    .map(({ item }) => item);
};

export const getCanonicalServiceTypeCode = (code) => {
  const normalizedCode = String(code || '').toUpperCase();
  return (
    getAllLineItemServiceTypes().find(
      (type) => type.code === normalizedCode || type.oldCode === normalizedCode
    )?.code || normalizedCode
  );
};

export const getLibraryItemGroupCode = (item) => {
  if (item?.kind === 'lineitem' || item?.isservice || item?.servicetypecode) {
    return getCanonicalServiceTypeCode(item?.servicetypecode);
  }
  return item?.code || '';
};

export const getLibraryResultCounts = (items = []) => {
  const counts = Object.fromEntries(
    LIBRARY_GROUP_ORDER.map((code) => [code, 0])
  );
  items.forEach((item) => {
    const code = getLibraryItemGroupCode(item);
    if (Object.hasOwn(counts, code)) counts[code] += 1;
  });

  return {
    ...counts,
    total: items.length,
    services: LIBRARY_GROUP_ORDER.filter(
      (code) => !['day', 'additional'].includes(code)
    ).reduce((total, code) => total + counts[code], 0)
  };
};

export const getPrioritizedLibraryGroupCodes = (query = '') =>
  getLibrarySearchPlan(query).prioritizedGroupCodes;

export const getPrioritizedServiceTypes = getPrioritizedLibraryGroupCodes;

export const sortLibraryItemsByType = (items = [], query = '') => {
  const hasActiveSearch =
    normalizeLibrarySearchText(query).length >= MIN_LIBRARY_SEARCH_LENGTH;
  const plan = getLibrarySearchPlan(query);
  const entries = getLibrarySearchEntries(items, plan);
  const matchedEntries = entries.filter(
    ({ tier }) => tier >= LIBRARY_MATCH_TIERS.EXACT_TITLE
  );
  const groupCodes = hasActiveSearch
    ? getOrderedGroupCodesFromEntries(
        matchedEntries.length > 0 ? matchedEntries : entries,
        plan
      )
    : getDefaultOrderedGroupCodes(items);
  const groupOrder = getGroupOrderMap(groupCodes);
  const compareEntries = hasActiveSearch
    ? compareLibraryEntries(groupOrder)
    : compareLibraryEntriesByGroupOnly(groupOrder);

  return entries.sort(compareEntries).map(({ item }) => item);
};
